import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import SearchableSelect from '../components/ui/SearchableSelect';
import { 
  GraduationCap, Plus, LogOut, FileText, Loader2, Eye, EyeOff, Upload, 
  Home, Users, Send, FolderOpen, Settings, ChevronRight, Search,
  User, Mail, Phone, Globe, Calendar, FileCheck, Building, BookOpen,
  ArrowRightLeft, Trash2, Edit, MoreHorizontal, CheckCircle, Clock,
  CreditCard, Sparkles, MessageCircle, Scan, DollarSign, Zap,
  Brain, History, Bell, AlertCircle, Bot, Files, X, Archive, RotateCcw
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgentPortalPage = () => {
  // Auth state
  const [agent, setAgent] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('agent_token'));
  const [permissions, setPermissions] = useState({
    upload_documents: true,
    view_own_applications: true,
    view_application_status: true,
    edit_applications: false,
    view_communications: false,
    view_documents: false
  });
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('dashboard');
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  
  // Applications state
  const [applications, setApplications] = useState([]);
  const [tabCounts, setTabCounts] = useState({ enquiry: 0, active: 0, forwarded: 0, archived: 0 });
  const [activeTab, setActiveTab] = useState('enquiry');
  const [searchQuery, setSearchQuery] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Unread chat tracking per application
  const [unreadChats, setUnreadChats] = useState({});
  
  // Master data
  const [masterData, setMasterData] = useState({
    universities: [],
    programs: [],
    intakes: [],
    countries: [],
    degree_types: [],
    document_types: [],
    cities: [],
    qualifications: []
  });
  
  // Create/Edit application state
  const [createOpen, setCreateOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({
    // Personal Information
    student_name: '',
    student_email: '',
    student_phone: '',
    student_phone_2: '',
    title: '',
    first_name: '',
    last_name: '',
    gender: '',
    date_of_birth: '',
    nationality: '',
    // ID Documents
    passport_number: '',
    passport_issue_date: '',
    passport_expiry_date: '',
    nic_number: '',
    nic_issue_date: '',
    nic_expiry_date: '',
    // Address Details
    address: '',
    postal_code: '',
    country_living_in: '',
    province: '',
    city: '',
    // Academic Details
    last_education_country: '',
    last_qualification: '',
    qualification_status: '',
    last_qualification_subject: '',
    last_qualification_faculty: '',
    last_qualification_grades: '',
    last_qualification_year: '',
    last_institution: '',
    last_institution_city: '',
    // English Test
    english_test_type: '',
    english_test_score: '',
    // University & Course Selection
    university_id: '',
    program_id: '',
    intake_id: '',
    degree_type: '',
    // Meta
    notes: '',
    agent_tab: 'enquiry'
  });
  
  // Detail view state
  const [selectedApp, setSelectedApp] = useState(null);
  const [appDocuments, setAppDocuments] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Document upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Documents to upload with new application
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [pendingDocFile, setPendingDocFile] = useState(null);
  const [pendingDocType, setPendingDocType] = useState('');
  
  // Transfer state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferringApp, setTransferringApp] = useState(null);

  // Credits state
  const [credits, setCredits] = useState({ balance: 0, total_purchased: 0, total_used: 0 });
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [purchaseAmount, setPurchaseAmount] = useState(10);
  const [purchasing, setPurchasing] = useState(false);

  // AI Features state
  const [aiScanOpen, setAiScanOpen] = useState(false);
  const [aiScanFile, setAiScanFile] = useState(null);
  const [aiScanDocType, setAiScanDocType] = useState('passport');
  const [aiScanning, setAiScanning] = useState(false);
  const [aiScanResult, setAiScanResult] = useState(null);

  // Chat state
  const [chatOpen, setChatOpen] = useState(false);
  const [chatAppId, setChatAppId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const [orgTeamMembers, setOrgTeamMembers] = useState([]);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [chatView, setChatView] = useState('select'); // 'select' or 'chat'
  const chatEndRef = useRef(null);

  // AI Counselor state
  const [aiCounselorOpen, setAiCounselorOpen] = useState(false);
  const [aiCounselorStep, setAiCounselorStep] = useState(1); // 1: Select Uni, 2: Upload Docs, 3: Review & Create
  const [aiSelectedUniversity, setAiSelectedUniversity] = useState(null);
  const [aiUploadedFiles, setAiUploadedFiles] = useState([]);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [aiProgress, setAiProgress] = useState(0);
  const [aiExtractedProfile, setAiExtractedProfile] = useState(null);
  const [aiSuggestedPrograms, setAiSuggestedPrograms] = useState([]);

  // Floating chat/AI bubbles
  const [floatingChatOpen, setFloatingChatOpen] = useState(false);
  const [floatingAiOpen, setFloatingAiOpen] = useState(false);

  // Reminder state
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderAppId, setReminderAppId] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [reminderStats, setReminderStats] = useState({});
  const [newReminder, setNewReminder] = useState({ date: '', message: '' });
  const [loadingReminders, setLoadingReminders] = useState(false);

  // Activity Log state
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityAppId, setActivityAppId] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const api = () => axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    if (token) {
      initializePortal();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Poll for unread chat messages
  useEffect(() => {
    if (!token) return;
    
    const fetchUnreadCount = async () => {
      try {
        const unreadRes = await api().get('/agents/portal/chat/unread-count');
        setUnreadChatCount(unreadRes.data.unread_count || 0);
      } catch (e) {}
    };
    
    // Initial fetch
    fetchUnreadCount();
    
    // Poll every 15 seconds
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, [token]);

  const initializePortal = async () => {
    try {
      // Fetch permissions
      const permRes = await api().get('/agents/permissions');
      setPermissions(permRes.data);
      
      // Fetch master data
      const masterRes = await api().get('/agents/portal/master-data');
      setMasterData(masterRes.data);
      
      // Fetch dashboard stats
      const statsRes = await api().get('/agents/portal/dashboard-stats');
      setDashboardStats(statsRes.data);
      
      // Fetch credit balance
      try {
        const creditsRes = await api().get('/agents/portal/credits/balance');
        setCredits(creditsRes.data);
      } catch (e) {
        console.log('Credits not available');
      }
      
      // Fetch unread chat count
      try {
        const unreadRes = await api().get('/agents/portal/chat/unread-count');
        setUnreadChatCount(unreadRes.data.unread_count || 0);
      } catch (e) {
        console.log('Chat count not available');
      }
      
      // Get agent info from token
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      setAgent({ id: tokenPayload.agent_id, username: tokenPayload.username });
      
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      console.error('Init error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async (tab = activeTab) => {
    try {
      const response = await api().get(`/agents/portal/applications?tab=${tab}&search=${searchQuery}`);
      setApplications(response.data.applications);
      setTabCounts(response.data.tab_counts);
      
      // Fetch unread chat counts for each application
      const unreadMap = {};
      for (const app of response.data.applications) {
        try {
          const chatRes = await api().get(`/agents/portal/chat/${app.id}/unread`);
          if (chatRes.data.unread_count > 0) {
            unreadMap[app.id] = chatRes.data.unread_count;
          }
        } catch (e) {}
      }
      setUnreadChats(unreadMap);
    } catch (error) {
      toast.error('Failed to load applications');
    }
  };

  useEffect(() => {
    if (token && activeView === 'applications') {
      fetchApplications();
    }
  }, [token, activeView, activeTab, searchQuery]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/agents/login`, loginData);
      const { access_token, agent: agentData, permissions: perms } = response.data;
      localStorage.setItem('agent_token', access_token);
      setToken(access_token);
      setAgent(agentData);
      setPermissions(perms || {});
      toast.success('Login successful');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('agent_token');
    setToken(null);
    setAgent(null);
    setApplications([]);
    setActiveView('dashboard');
    toast.success('Logged out');
  };

  const resetFormData = () => {
    setFormData({
      // Personal Information
      student_name: '',
      student_email: '',
      student_phone: '',
      student_phone_2: '',
      title: '',
      first_name: '',
      last_name: '',
      gender: '',
      date_of_birth: '',
      nationality: '',
      // ID Documents
      passport_number: '',
      passport_issue_date: '',
      passport_expiry_date: '',
      nic_number: '',
      nic_issue_date: '',
      nic_expiry_date: '',
      // Address Details
      address: '',
      postal_code: '',
      country_living_in: '',
      province: '',
      city: '',
      // Academic Details
      last_education_country: '',
      last_qualification: '',
      qualification_status: '',
      last_qualification_subject: '',
      last_qualification_faculty: '',
      last_qualification_grades: '',
      last_qualification_year: '',
      last_institution: '',
      last_institution_city: '',
      // English Test
      english_test_type: '',
      english_test_score: '',
      // University & Course Selection
      university_id: '',
      program_id: '',
      intake_id: '',
      degree_type: '',
      // Meta
      notes: '',
      agent_tab: 'enquiry'
    });
  };

  const handleCreateApplication = async (e) => {
    e.preventDefault();
    try {
      const response = await api().post('/agents/portal/applications', formData);
      const newAppId = response.data.id;
      
      // Upload pending documents if any
      if (pendingDocuments.length > 0) {
        for (const doc of pendingDocuments) {
          const docFormData = new FormData();
          docFormData.append('file', doc.file);
          if (doc.type) docFormData.append('document_type', doc.type);
          
          try {
            await api().post(`/agents/application/${newAppId}/documents`, docFormData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (docErr) {
            console.error('Failed to upload document:', doc.file.name, docErr);
          }
        }
        toast.success(`Application created with ${pendingDocuments.length} document(s)`);
      } else {
        toast.success('Application created successfully');
      }
      
      setCreateOpen(false);
      resetFormData();
      setPendingDocuments([]); // Clear pending documents
      fetchApplications();
      // Refresh dashboard stats
      const statsRes = await api().get('/agents/portal/dashboard-stats');
      setDashboardStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    }
  };

  const handleUpdateApplication = async (e) => {
    e.preventDefault();
    if (!editingApp) return;
    try {
      await api().put(`/agents/portal/applications/${editingApp.id}`, formData);
      toast.success('Application updated successfully');
      setEditingApp(null);
      setCreateOpen(false);
      resetFormData();
      fetchApplications();
      if (selectedApp?.id === editingApp.id) {
        fetchApplicationDetail(editingApp.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    }
  };

  // Add document to pending list for new application
  const addPendingDocument = () => {
    if (!pendingDocFile) return;
    setPendingDocuments([...pendingDocuments, { file: pendingDocFile, type: pendingDocType, name: pendingDocFile.name }]);
    setPendingDocFile(null);
    setPendingDocType('');
    toast.success(`Added: ${pendingDocFile.name}`);
  };

  // Remove document from pending list
  const removePendingDocument = (index) => {
    const newDocs = [...pendingDocuments];
    newDocs.splice(index, 1);
    setPendingDocuments(newDocs);
  };

  const fetchApplicationDetail = async (appId) => {
    setDetailLoading(true);
    try {
      const response = await api().get(`/agents/portal/applications/${appId}`);
      setSelectedApp(response.data.application);
      setAppDocuments(response.data.documents);
      setRequiredDocs(response.data.required_documents);
      setActiveView('detail');
    } catch (error) {
      toast.error('Failed to load application details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!transferringApp) return;
    try {
      await api().post(`/agents/portal/applications/${transferringApp.id}/transfer`);
      toast.success('Application transferred to organization');
      setTransferOpen(false);
      setTransferringApp(null);
      fetchApplications();
      // Refresh stats
      const statsRes = await api().get('/agents/portal/dashboard-stats');
      setDashboardStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer application');
    }
  };

  const handleMoveTab = async (appId, targetTab) => {
    try {
      await api().post(`/agents/portal/applications/${appId}/move-tab`, { target_tab: targetTab });
      toast.success(`Moved to ${targetTab}`);
      fetchApplications();
    } catch (error) {
      toast.error('Failed to move application');
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!uploadFile || !selectedApp) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadDocType) formData.append('document_type', uploadDocType);
      
      await api().post(`/agents/application/${selectedApp.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Document uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDocType('');
      fetchApplicationDetail(selectedApp.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // ==========================================
  // CREDIT FUNCTIONS
  // ==========================================

  const fetchCredits = async () => {
    try {
      const response = await api().get('/agents/portal/credits/balance');
      setCredits(response.data);
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };

  const handlePurchaseCredits = async () => {
    setPurchasing(true);
    try {
      const response = await api().post('/agents/portal/credits/purchase', {
        credits: purchaseAmount
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  // Check for payment success on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success(`Credits purchased successfully!`);
      fetchCredits();
      // Clean URL
      window.history.replaceState({}, '', '/agent-portal');
    }
  }, []);

  // ==========================================
  // AI SCAN FUNCTIONS
  // ==========================================

  const handleAiScan = async () => {
    if (!aiScanFile) {
      toast.error('Please select a file');
      return;
    }
    
    setAiScanning(true);
    setAiScanResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', aiScanFile);
      formData.append('document_type', aiScanDocType);
      
      const response = await api().post('/agents/portal/ai/extract-document', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAiScanResult(response.data);
      setCredits(prev => ({ ...prev, balance: response.data.remaining_credits }));
      toast.success(`Document scanned! Used ${response.data.credits_used} credit`);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error('Insufficient credits. Please purchase more credits.');
        setCreditsOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Scan failed');
      }
    } finally {
      setAiScanning(false);
    }
  };

  const applyAiScanToForm = () => {
    if (!aiScanResult?.extracted_data) return;
    
    const data = aiScanResult.extracted_data;
    const updates = {};
    
    // Map extracted data to form fields
    if (data.full_name) updates.student_name = data.full_name;
    if (data.first_name) updates.first_name = data.first_name;
    if (data.last_name) updates.last_name = data.last_name;
    if (data.email) updates.student_email = data.email;
    if (data.phone) updates.student_phone = data.phone;
    if (data.passport_number) updates.passport_number = data.passport_number;
    if (data.passport_issue_date) updates.passport_issue_date = data.passport_issue_date;
    if (data.passport_expiry_date) updates.passport_expiry_date = data.passport_expiry_date;
    if (data.nic_number) updates.nic_number = data.nic_number;
    if (data.nic_issue_date) updates.nic_issue_date = data.nic_issue_date;
    if (data.nic_expiry_date) updates.nic_expiry_date = data.nic_expiry_date;
    if (data.date_of_birth) updates.date_of_birth = data.date_of_birth;
    if (data.gender) updates.gender = data.gender.toLowerCase();
    if (data.nationality) updates.nationality = data.nationality;
    if (data.address) updates.address = data.address;
    if (data.city) updates.city = data.city;
    if (data.province) updates.province = data.province;
    if (data.father_name) updates.father_name = data.father_name;
    if (data.last_qualification) updates.last_qualification = data.last_qualification;
    if (data.last_institution) updates.last_institution = data.last_institution;
    if (data.qualification_year) updates.last_qualification_year = data.qualification_year;
    if (data.english_test_type) updates.english_test_type = data.english_test_type.toLowerCase();
    if (data.english_test_score) updates.english_test_score = data.english_test_score;
    
    setFormData(prev => ({ ...prev, ...updates }));
    toast.success(`Applied ${Object.keys(updates).length} fields from scan`);
    setAiScanOpen(false);
    setAiScanResult(null);
    setAiScanFile(null);
  };

  // ==========================================
  // CHAT FUNCTIONS
  // ==========================================

  const openChat = async (appId) => {
    setChatAppId(appId);
    setChatOpen(true);
    setChatMessages([]);
    setChatView('select');
    setSelectedCounselor(null);
    
    try {
      const response = await api().get(`/agents/portal/chat/${appId}/messages`);
      setChatMessages(response.data.messages || []);
      setOrgTeamMembers(response.data.team_members || []);
      
      // Mark as read
      await api().put(`/agents/portal/chat/${appId}/mark-read`);
      setUnreadChatCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to load chat');
    }
  };

  const selectCounselor = (member) => {
    setSelectedCounselor(member);
    setChatView('chat');
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || !chatAppId) return;
    
    setChatSending(true);
    try {
      const response = await api().post(`/agents/portal/chat/${chatAppId}/messages`, {
        message: chatInput.trim(),
        to_user_id: selectedCounselor?.id // Optional: specify recipient
      });
      
      setChatMessages(prev => [...prev, response.data]);
      setChatInput('');
      
      // Scroll to bottom
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setChatSending(false);
    }
  };

  // Poll for new messages when chat is open
  useEffect(() => {
    if (!chatOpen || !chatAppId) return;
    
    const interval = setInterval(async () => {
      try {
        const response = await api().get(`/agents/portal/chat/${chatAppId}/messages`);
        setChatMessages(response.data.messages || []);
      } catch (e) {}
    }, 5000);
    
    return () => clearInterval(interval);
  }, [chatOpen, chatAppId]);

  // ==========================================
  // REMINDER FUNCTIONS
  // ==========================================

  const openReminder = async (appId) => {
    setReminderAppId(appId);
    setReminderOpen(true);
    setLoadingReminders(true);
    setReminders([]);
    
    try {
      const response = await api().get(`/agents/portal/reminders/${appId}`);
      setReminders(response.data.reminders || []);
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoadingReminders(false);
    }
  };

  const createReminder = async () => {
    if (!newReminder.date) {
      toast.error('Please select a date');
      return;
    }
    
    try {
      await api().post(`/agents/portal/reminders/${reminderAppId}`, {
        date: newReminder.date,
        message: newReminder.message
      });
      toast.success('Reminder created');
      setNewReminder({ date: '', message: '' });
      // Refresh reminders
      const response = await api().get(`/agents/portal/reminders/${reminderAppId}`);
      setReminders(response.data.reminders || []);
      // Update stats
      fetchReminderStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reminder');
    }
  };

  const fetchReminderStats = async () => {
    if (applications.length === 0) return;
    
    const appIds = applications.map(a => a.id).join(',');
    try {
      const response = await api().get(`/agents/portal/reminders/stats/batch?app_ids=${appIds}`);
      setReminderStats(response.data.stats || {});
    } catch (error) {
      console.error('Failed to fetch reminder stats:', error);
    }
  };

  // Fetch reminder stats when applications change
  useEffect(() => {
    if (token && applications.length > 0) {
      fetchReminderStats();
    }
  }, [applications]);

  // ==========================================
  // ACTIVITY LOG FUNCTIONS
  // ==========================================

  const openActivityLog = async (appId) => {
    setActivityAppId(appId);
    setActivityOpen(true);
    setLoadingActivity(true);
    setActivityLogs([]);
    
    try {
      const response = await api().get(`/agents/portal/activity-log/${appId}`);
      setActivityLogs(response.data.logs || []);
    } catch (error) {
      toast.error('Failed to load activity log');
    } finally {
      setLoadingActivity(false);
    }
  };

  // ==========================================
  // AI COUNSELOR FUNCTIONS
  // ==========================================

  const resetAiCounselor = () => {
    setAiCounselorStep(1);
    setAiSelectedUniversity(null);
    setAiUploadedFiles([]);
    setAiProcessing(false);
    setAiProgress(0);
    setAiExtractedProfile(null);
    setAiSuggestedPrograms([]);
  };

  const handleAiCounselorAnalyze = async () => {
    if (!aiSelectedUniversity || aiUploadedFiles.length === 0) {
      toast.error('Please select a university and upload documents');
      return;
    }

    if (credits.balance < 3) {
      toast.error('Insufficient credits. AI Counselor requires 3 credits.');
      setCreditsOpen(true);
      return;
    }

    setAiProcessing(true);
    setAiProgress(0);

    // Simulate progress
    const progressInterval = setInterval(() => {
      setAiProgress(prev => Math.min(prev + 10, 90));
    }, 500);

    try {
      const formData = new FormData();
      formData.append('university_id', aiSelectedUniversity.id);
      aiUploadedFiles.forEach(f => formData.append('files', f));

      const response = await api().post('/agents/portal/ai-counselor/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      clearInterval(progressInterval);
      setAiProgress(100);

      setAiExtractedProfile(response.data.student_profile || {});
      setAiSuggestedPrograms(response.data.suggested_programs || []);
      setCredits(prev => ({ ...prev, balance: response.data.remaining_credits }));
      
      toast.success(`Analysis complete! Used ${response.data.credits_used} credits`);
      setAiCounselorStep(3);
    } catch (error) {
      clearInterval(progressInterval);
      if (error.response?.status === 402) {
        toast.error('Insufficient credits');
        setCreditsOpen(true);
      } else {
        toast.error(error.response?.data?.detail || 'Analysis failed');
      }
    } finally {
      setAiProcessing(false);
    }
  };

  const handleAiCounselorCreateApp = async (programId, degreeType) => {
    try {
      const response = await api().post('/agents/portal/ai-counselor/create-application', {
        student_profile: aiExtractedProfile,
        university_id: aiSelectedUniversity.id,
        program_id: programId,
        degree_type: degreeType
      });

      toast.success('Application created successfully!');
      setAiCounselorOpen(false);
      resetAiCounselor();
      fetchApplications();
      fetchApplicationDetail(response.data.application_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create application');
    }
  };

  // Helper to get status color
  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800',
      'submitted': 'bg-blue-100 text-blue-800',
      'in_review': 'bg-yellow-100 text-yellow-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'documents_pending': 'bg-orange-100 text-orange-800',
      'offer_received': 'bg-amber-100 text-slate-800',
      'visa_applied': 'bg-amber-100 text-slate-800',
      'enrolled': 'bg-emerald-100 text-emerald-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const openEditDialog = (app) => {
    setEditingApp(app);
    setFormData({
      // Personal Information
      student_name: app.student_name || '',
      student_email: app.student_email || '',
      student_phone: app.student_phone || '',
      student_phone_2: app.student_phone_2 || '',
      title: app.title || '',
      first_name: app.first_name || '',
      last_name: app.last_name || '',
      gender: app.gender || '',
      date_of_birth: app.date_of_birth || '',
      nationality: app.nationality || '',
      // ID Documents
      passport_number: app.passport_number || '',
      passport_issue_date: app.passport_issue_date || '',
      passport_expiry_date: app.passport_expiry_date || '',
      nic_number: app.nic_number || '',
      nic_issue_date: app.nic_issue_date || '',
      nic_expiry_date: app.nic_expiry_date || '',
      // Address Details
      address: app.address || '',
      postal_code: app.postal_code || '',
      country_living_in: app.country_living_in || '',
      province: app.province || '',
      city: app.city || '',
      // Academic Details
      last_education_country: app.last_education_country || '',
      last_qualification: app.last_qualification || '',
      qualification_status: app.qualification_status || '',
      last_qualification_subject: app.last_qualification_subject || '',
      last_qualification_faculty: app.last_qualification_faculty || '',
      last_qualification_grades: app.last_qualification_grades || '',
      last_qualification_year: app.last_qualification_year || '',
      last_institution: app.last_institution || '',
      last_institution_city: app.last_institution_city || '',
      // English Test
      english_test_type: app.english_test_type || '',
      english_test_score: app.english_test_score || '',
      // University & Course Selection
      university_id: app.university_id || '',
      program_id: app.program_id || '',
      intake_id: app.intake_id || '',
      degree_type: app.degree_type || '',
      // Meta
      notes: app.notes || '',
      agent_tab: app.agent_tab || 'enquiry'
    });
    setCreateOpen(true);
  };

  // Filter programs by university - if university is selected, show only matching programs
  // If no programs match the selected university, show all programs (handles legacy data)
  const matchingPrograms = masterData.programs.filter(p => 
    p.university_id === formData.university_id || p.parent_id === formData.university_id
  );
  const filteredPrograms = formData.university_id 
    ? (matchingPrograms.length > 0 ? matchingPrograms : masterData.programs)
    : masterData.programs;
  
  // Filter intakes similarly
  const matchingIntakes = masterData.intakes.filter(i => 
    i.university_id === formData.university_id || i.parent_id === formData.university_id
  );
  const filteredIntakes = formData.university_id
    ? (matchingIntakes.length > 0 ? matchingIntakes : masterData.intakes)
    : masterData.intakes;

  // ============ LOGIN PAGE ============
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
        <Toaster position="top-right" richColors />
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-amber-500 shadow-xl shadow-amber-500/30">
              <GraduationCap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-3xl text-white tracking-tight">Agent Portal</h1>
              <p className="text-sm text-slate-400">EduBridge Network Partner Access</p>
            </div>
          </div>

          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur shadow-2xl">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-2xl text-white">Welcome Back</CardTitle>
              <CardDescription className="text-slate-400">Sign in to manage your applications</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Username</Label>
                  <Input
                    value={loginData.username}
                    onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                    required
                    className="bg-slate-700/50 border-slate-600 text-white"
                    data-testid="agent-login-username"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      className="bg-slate-700/50 border-slate-600 text-white pr-10"
                      data-testid="agent-login-password"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 text-slate-400 hover:text-white"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-amber-500 hover:from-amber-500 hover:to-slate-800" data-testid="agent-login-submit">
                  Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ============ MAIN PORTAL LAYOUT ============
  return (
    <div className="min-h-screen bg-background flex">
      <Toaster position="top-right" richColors />
      
      {/* Sidebar */}
      <aside className="w-64 border-r bg-muted/30 flex flex-col">
        <div className="p-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-500">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Agent Portal</h1>
              <p className="text-xs text-muted-foreground">{agent?.username}</p>
            </div>
          </div>
        </div>
        
        {/* Credit Balance Card */}
        <div className="p-4 border-b">
          <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-lg p-3 border border-emerald-500/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium">AI Credits</span>
              </div>
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30">
                {credits.balance}
              </Badge>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="w-full mt-2 text-emerald-600 hover:bg-emerald-500/10"
              onClick={() => setCreditsOpen(true)}
            >
              <CreditCard className="w-3 h-3 mr-2" />
              Buy Credits
            </Button>
          </div>
        </div>
        
        <ScrollArea className="flex-1 p-4">
          <nav className="space-y-2">
            <Button
              variant={activeView === 'dashboard' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => setActiveView('dashboard')}
            >
              <Home className="w-4 h-4 mr-3" />
              Dashboard
            </Button>
            <Button
              variant={activeView === 'applications' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => { setActiveView('applications'); setActiveTab('enquiry'); }}
            >
              <FileText className="w-4 h-4 mr-3" />
              Applications
              {tabCounts.enquiry + tabCounts.active > 0 && (
                <Badge className="ml-auto" variant="secondary">{tabCounts.enquiry + tabCounts.active}</Badge>
              )}
            </Button>
            <Button
              variant={activeView === 'forwarded' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => { setActiveView('applications'); setActiveTab('forwarded'); }}
            >
              <Send className="w-4 h-4 mr-3" />
              Forwarded
              {tabCounts.forwarded > 0 && (
                <Badge className="ml-auto" variant="outline">{tabCounts.forwarded}</Badge>
              )}
            </Button>
            <Button
              variant={activeView === 'archived' ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => { setActiveView('applications'); setActiveTab('archived'); }}
            >
              <Archive className="w-4 h-4 mr-3" />
              Archived
              {tabCounts.archived > 0 && (
                <Badge className="ml-auto" variant="outline">{tabCounts.archived}</Badge>
              )}
            </Button>
            
            <Separator className="my-3" />
            
            <p className="text-xs font-medium text-muted-foreground px-2 mb-2">AI TOOLS</p>
            <Button
              variant="ghost"
              className="w-full justify-start text-amber-500 hover:bg-amber-500/10"
              onClick={() => { resetAiCounselor(); setAiCounselorOpen(true); }}
            >
              <Brain className="w-4 h-4 mr-3" />
              AI Counselor
              <Badge className="ml-auto text-xs" variant="outline">3 credits</Badge>
            </Button>
          </nav>
        </ScrollArea>
        
        <div className="p-4 border-t">
          <Button variant="outline" className="w-full" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : activeView === 'dashboard' ? (
          // ============ DASHBOARD VIEW ============
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Welcome back!</h2>
                <p className="text-muted-foreground">Here's an overview of your applications</p>
              </div>
              <Button onClick={() => { resetFormData(); setEditingApp(null); setCreateOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Applications</p>
                      <p className="text-3xl font-bold">{dashboardStats?.total || 0}</p>
                    </div>
                    <FileText className="w-10 h-10 text-blue-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Enquiries</p>
                      <p className="text-3xl font-bold">{dashboardStats?.enquiry || 0}</p>
                    </div>
                    <Clock className="w-10 h-10 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Active</p>
                      <p className="text-3xl font-bold">{dashboardStats?.active || 0}</p>
                    </div>
                    <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border-amber-500/20">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Forwarded</p>
                      <p className="text-3xl font-bold">{dashboardStats?.forwarded || 0}</p>
                    </div>
                    <Send className="w-10 h-10 text-amber-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Applications */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Applications</CardTitle>
                <CardDescription>Your latest submitted applications</CardDescription>
              </CardHeader>
              <CardContent>
                {dashboardStats?.recent_applications?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>University</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dashboardStats.recent_applications.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">{app.student_name}</TableCell>
                          <TableCell>{app.university_name || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{app.agent_tab}</Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(app.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openChat(app.id)} title="Chat with team">
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => fetchApplicationDetail(app.id)}>
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No applications yet</p>
                    <Button variant="link" onClick={() => { resetFormData(); setCreateOpen(true); }}>
                      Create your first application
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        ) : activeView === 'applications' ? (
          // ============ APPLICATIONS VIEW ============
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold">Applications</h2>
                <p className="text-muted-foreground">Manage your student applications</p>
              </div>
              <Button onClick={() => { resetFormData(); setEditingApp(null); setCreateOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex items-center justify-between">
                <TabsList>
                  <TabsTrigger value="enquiry" className="gap-2">
                    Enquiry
                    <Badge variant="secondary">{tabCounts.enquiry}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="active" className="gap-2">
                    Active Applications
                    <Badge variant="secondary">{tabCounts.active}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="forwarded" className="gap-2">
                    My Forwarded Cases
                    <Badge variant="outline">{tabCounts.forwarded}</Badge>
                  </TabsTrigger>
                  <TabsTrigger value="archived" className="gap-2">
                    <Archive className="w-4 h-4" />
                    Archived
                    <Badge variant="outline">{tabCounts.archived}</Badge>
                  </TabsTrigger>
                </TabsList>
                
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 w-64"
                    />
                  </div>
                </div>
              </div>

              <TabsContent value={activeTab} className="mt-4">
                <Card>
                  <CardContent className="p-0">
                    {applications.length > 0 ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Student</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>University</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Action Center</TableHead>
                            <TableHead className="w-[100px]">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {applications.map((app) => {
                            const appReminderStats = reminderStats[app.id] || { sent: 0, pending: 0 };
                            const hasUnreadChat = unreadChats[app.id] > 0;
                            return (
                            <TableRow 
                              key={app.id} 
                              className={`cursor-pointer hover:bg-muted/50 ${hasUnreadChat ? 'bg-blue-50/50 dark:bg-blue-950/20 border-l-2 border-l-blue-500' : ''}`} 
                              onClick={() => fetchApplicationDetail(app.id)}
                            >
                              <TableCell className="font-medium">{app.student_name}</TableCell>
                              <TableCell>{app.student_email}</TableCell>
                              <TableCell>{app.university_name || '-'}</TableCell>
                              <TableCell>
                                <Badge className={getStatusColor(app.status)}>
                                  {app.status?.replace(/_/g, ' ') || 'draft'}
                                </Badge>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <TooltipProvider>
                                  <div className="flex items-center gap-1">
                                    {/* Chat Icon with unread indicator */}
                                    <div className="relative">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon" className={`h-7 w-7 ${hasUnreadChat ? 'text-blue-600' : 'text-blue-600'}`} onClick={() => openChat(app.id)}>
                                            <MessageCircle className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>{hasUnreadChat ? `${unreadChats[app.id]} unread message(s)` : 'Chat with Organization'}</TooltipContent>
                                      </Tooltip>
                                      {hasUnreadChat && (
                                        <div className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] font-bold animate-pulse">
                                          {unreadChats[app.id] > 9 ? '9+' : unreadChats[app.id]}
                                        </div>
                                      )}
                                    </div>
                                    
                                    {/* Reminder Icon with counters */}
                                    <div className="relative flex items-center">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openReminder(app.id)}>
                                            <Bell className="w-4 h-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Reminders</TooltipContent>
                                      </Tooltip>
                                      <div className="flex items-center gap-0.5 ml-[-4px]">
                                        {appReminderStats.sent > 0 && (
                                          <div className="flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold">
                                            {appReminderStats.sent > 9 ? '9+' : appReminderStats.sent}
                                          </div>
                                        )}
                                        {appReminderStats.pending > 0 && (
                                          <div className="flex items-center justify-center w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold">
                                            {appReminderStats.pending > 9 ? '9+' : appReminderStats.pending}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Activity Log Icon */}
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-600" onClick={() => openActivityLog(app.id)}>
                                          <History className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent>Activity Log</TooltipContent>
                                    </Tooltip>
                                  </div>
                                </TooltipProvider>
                              </TableCell>
                              <TableCell onClick={(e) => e.stopPropagation()}>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon">
                                      <MoreHorizontal className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem onClick={() => fetchApplicationDetail(app.id)}>
                                      <Eye className="w-4 h-4 mr-2" />
                                      View Details
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => openEditDialog(app)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    {!app.transferred_to_org && (
                                      <>
                                        {activeTab === 'enquiry' && (
                                          <DropdownMenuItem onClick={() => handleMoveTab(app.id, 'active')}>
                                            <ArrowRightLeft className="w-4 h-4 mr-2" />
                                            Move to Active
                                          </DropdownMenuItem>
                                        )}
                                        {activeTab === 'active' && (
                                          <DropdownMenuItem onClick={() => handleMoveTab(app.id, 'enquiry')}>
                                            <ArrowRightLeft className="w-4 h-4 mr-2" />
                                            Move to Enquiry
                                          </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem onClick={() => { setTransferringApp(app); setTransferOpen(true); }} className="text-amber-500">
                                          <Send className="w-4 h-4 mr-2" />
                                          Forward to Organization
                                        </DropdownMenuItem>
                                      </>
                                    )}
                                    {app.transferred_to_org && (
                                      <DropdownMenuItem onClick={() => { setTransferringApp(app); setTransferOpen(true); }} className="text-orange-600">
                                        <RotateCcw className="w-4 h-4 mr-2" />
                                        Re-Forward to Organization
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          )})}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="text-center py-12 text-muted-foreground">
                        <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No applications in this tab</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        ) : activeView === 'detail' && selectedApp ? (
          // ============ APPLICATION DETAIL VIEW ============
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => { setActiveView('applications'); setSelectedApp(null); }}>
                  ← Back
                </Button>
                <div>
                  <h2 className="text-2xl font-bold">{selectedApp.student_name}</h2>
                  <p className="text-muted-foreground">{selectedApp.student_email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!selectedApp.transferred_to_org && (
                  <>
                    <Button variant="outline" onClick={() => openEditDialog(selectedApp)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button onClick={() => { setTransferringApp(selectedApp); setTransferOpen(true); }}>
                      <Send className="w-4 h-4 mr-2" />
                      Transfer to Organization
                    </Button>
                  </>
                )}
                {selectedApp.transferred_to_org && (
                  <>
                    <Button variant="outline" onClick={() => openEditDialog(selectedApp)}>
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => { setTransferringApp(selectedApp); setTransferOpen(true); }}>
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Re-Forward
                    </Button>
                    <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                      Forwarded on {new Date(selectedApp.transfer_date).toLocaleDateString()}
                    </Badge>
                  </>
                )}
              </div>
            </div>

            {/* Section 1: Basic Profiling */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  Section 1: Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <Label className="text-muted-foreground text-xs">Title</Label>
                    <p className="font-medium">{selectedApp.title || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">First Name</Label>
                    <p className="font-medium">{selectedApp.first_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Last Name</Label>
                    <p className="font-medium">{selectedApp.last_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Full Name</Label>
                    <p className="font-medium">{selectedApp.student_name}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Email</Label>
                    <p className="font-medium">{selectedApp.student_email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Phone</Label>
                    <p className="font-medium">{selectedApp.student_phone || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Phone 2</Label>
                    <p className="font-medium">{selectedApp.student_phone_2 || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Gender</Label>
                    <p className="font-medium capitalize">{selectedApp.gender || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Date of Birth</Label>
                    <p className="font-medium">{selectedApp.date_of_birth || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Nationality</Label>
                    <p className="font-medium">{selectedApp.nationality || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 2: ID Documents */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Section 2: ID Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-muted-foreground text-xs">Passport Number</Label>
                    <p className="font-medium">{selectedApp.passport_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Passport Issue Date</Label>
                    <p className="font-medium">{selectedApp.passport_issue_date || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Passport Expiry Date</Label>
                    <p className="font-medium">{selectedApp.passport_expiry_date || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">NIC Number</Label>
                    <p className="font-medium">{selectedApp.nic_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">NIC Issue Date</Label>
                    <p className="font-medium">{selectedApp.nic_issue_date || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">NIC Expiry Date</Label>
                    <p className="font-medium">{selectedApp.nic_expiry_date || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Address Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Section 3: Address Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2">
                    <Label className="text-muted-foreground text-xs">Address</Label>
                    <p className="font-medium">{selectedApp.address || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Postal Code</Label>
                    <p className="font-medium">{selectedApp.postal_code || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">City</Label>
                    <p className="font-medium">{selectedApp.city || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Province/State</Label>
                    <p className="font-medium">{selectedApp.province || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Country Living In</Label>
                    <p className="font-medium">{selectedApp.country_living_in || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Academic Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Section 4: Academic Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-muted-foreground text-xs">Last Education Country</Label>
                    <p className="font-medium">{selectedApp.last_education_country || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Last Qualification</Label>
                    <p className="font-medium capitalize">{selectedApp.last_qualification?.replace(/_/g, ' ') || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Qualification Status</Label>
                    <p className="font-medium capitalize">{selectedApp.qualification_status || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Subject</Label>
                    <p className="font-medium">{selectedApp.last_qualification_subject || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Faculty</Label>
                    <p className="font-medium">{selectedApp.last_qualification_faculty || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Grades/GPA</Label>
                    <p className="font-medium">{selectedApp.last_qualification_grades || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Qualification Year</Label>
                    <p className="font-medium">{selectedApp.last_qualification_year || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Last Institution Name</Label>
                    <p className="font-medium">{selectedApp.last_institution || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Institution City</Label>
                    <p className="font-medium">{selectedApp.last_institution_city || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 5: English Test */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Section 5: English Test
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-muted-foreground text-xs">English Test Type</Label>
                    <p className="font-medium uppercase">{selectedApp.english_test_type || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Test Score</Label>
                    <p className="font-medium">{selectedApp.english_test_score || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 6: University & Course Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Section 6: University & Course Selection
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div>
                    <Label className="text-muted-foreground text-xs">University</Label>
                    <p className="font-medium">{selectedApp.university_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Program</Label>
                    <p className="font-medium">{selectedApp.program_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Intake</Label>
                    <p className="font-medium">{selectedApp.intake_name || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Degree Type</Label>
                    <p className="font-medium">{selectedApp.degree_type || '-'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 7: Documents */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5" />
                  Section 7: Documents
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload Area - Always visible if permission granted */}
                {!selectedApp.transferred_to_org && permissions.upload_documents && (
                  <div className="border-2 border-dashed border-primary/30 rounded-lg p-6 bg-primary/5">
                    <div className="text-center">
                      <Upload className="w-10 h-10 mx-auto mb-3 text-primary/60" />
                      <p className="font-medium text-primary mb-1">Upload Documents</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        Click below to add passport, transcripts, certificates, or any required documents
                      </p>
                      <Button onClick={() => setUploadOpen(true)} className="bg-primary hover:bg-primary/90">
                        <Upload className="w-4 h-4 mr-2" />
                        Select Files to Upload
                      </Button>
                    </div>
                  </div>
                )}

                {/* Permission denied message */}
                {permissions.upload_documents === false && (
                  <div className="border border-amber-500/30 rounded-lg p-4 bg-amber-500/5">
                    <p className="text-sm text-amber-600 text-center">
                      Document upload is not enabled for your account. Contact your organization admin to request access.
                    </p>
                  </div>
                )}

                {requiredDocs.length > 0 && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-sm font-medium text-amber-600 mb-2">Required Documents:</p>
                    <div className="flex flex-wrap gap-2">
                      {requiredDocs.map((doc, idx) => (
                        <Badge key={idx} variant="outline" className="border-amber-500/30 text-amber-600">{doc}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {appDocuments.length > 0 ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">Uploaded Documents ({appDocuments.length})</p>
                    {appDocuments.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{doc.filename}</p>
                            <p className="text-xs text-muted-foreground">{doc.document_type || 'Unknown type'}</p>
                          </div>
                        </div>
                        <Badge variant="outline">{doc.status || 'pending'}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No documents uploaded yet</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {selectedApp.notes && (
              <Card>
                <CardHeader>
                  <CardTitle>Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{selectedApp.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : null}
      </main>

      {/* Create/Edit Application Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingApp ? 'Edit Application' : 'Create New Application'}</DialogTitle>
            <DialogDescription>Fill in the student details across all sections</DialogDescription>
          </DialogHeader>
          <form onSubmit={editingApp ? handleUpdateApplication : handleCreateApplication} className="space-y-6">
            {/* Section 1: Personal Information */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <User className="w-4 h-4" />
                Section 1: Personal Information
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Select value={formData.title} onValueChange={(v) => setFormData({ ...formData, title: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mr">Mr</SelectItem>
                      <SelectItem value="ms">Ms</SelectItem>
                      <SelectItem value="mrs">Mrs</SelectItem>
                      <SelectItem value="dr">Dr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>First Name</Label>
                  <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Last Name</Label>
                  <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Full Name *</Label>
                  <Input value={formData.student_name} onChange={(e) => setFormData({ ...formData, student_name: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={formData.student_email} onChange={(e) => setFormData({ ...formData, student_email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={formData.student_phone} onChange={(e) => setFormData({ ...formData, student_phone: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Phone 2</Label>
                  <Input value={formData.student_phone_2} onChange={(e) => setFormData({ ...formData, student_phone_2: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Nationality</Label>
                  <SearchableSelect
                    value={formData.nationality}
                    onChange={(v) => setFormData({ ...formData, nationality: v })}
                    options={masterData.countries.map((c) => ({ value: c.id, label: c.value || c.name }))}
                    placeholder="Select nationality"
                    searchPlaceholder="Search countries..."
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 2: ID Documents */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <FileText className="w-4 h-4" />
                Section 2: ID Documents
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Passport Number</Label>
                  <Input value={formData.passport_number} onChange={(e) => setFormData({ ...formData, passport_number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Passport Issue Date</Label>
                  <Input type="date" value={formData.passport_issue_date} onChange={(e) => setFormData({ ...formData, passport_issue_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Passport Expiry Date</Label>
                  <Input type="date" value={formData.passport_expiry_date} onChange={(e) => setFormData({ ...formData, passport_expiry_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>NIC Number</Label>
                  <Input value={formData.nic_number} onChange={(e) => setFormData({ ...formData, nic_number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>NIC Issue Date</Label>
                  <Input type="date" value={formData.nic_issue_date} onChange={(e) => setFormData({ ...formData, nic_issue_date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>NIC Expiry Date</Label>
                  <Input type="date" value={formData.nic_expiry_date} onChange={(e) => setFormData({ ...formData, nic_expiry_date: e.target.value })} />
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 3: Address Details */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <Globe className="w-4 h-4" />
                Section 3: Address Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Address</Label>
                  <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Postal Code</Label>
                  <Input value={formData.postal_code} onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>City</Label>
                  {masterData.cities.length > 0 ? (
                    <Select value={formData.city} onValueChange={(v) => setFormData({ ...formData, city: v })}>
                      <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>
                        {masterData.cities.map((c) => (<SelectItem key={c.id} value={c.value}>{c.value}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} placeholder="Enter city" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Province/State</Label>
                  <Input value={formData.province} onChange={(e) => setFormData({ ...formData, province: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Country Living In</Label>
                  <SearchableSelect
                    value={formData.country_living_in}
                    onChange={(v) => setFormData({ ...formData, country_living_in: v })}
                    options={masterData.countries.map((c) => ({ value: c.id, label: c.value || c.name }))}
                    placeholder="Select country"
                    searchPlaceholder="Search countries..."
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 4: Academic Details */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <BookOpen className="w-4 h-4" />
                Section 4: Academic Details
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Last Education Country</Label>
                  <SearchableSelect
                    value={formData.last_education_country}
                    onChange={(v) => setFormData({ ...formData, last_education_country: v })}
                    options={masterData.countries.map((c) => ({ value: c.id, label: c.value || c.name }))}
                    placeholder="Select country"
                    searchPlaceholder="Search countries..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Qualification</Label>
                  <Select value={formData.last_qualification} onValueChange={(v) => setFormData({ ...formData, last_qualification: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {masterData.qualifications && masterData.qualifications.length > 0 ? (
                        masterData.qualifications.map((q) => (<SelectItem key={q.id} value={q.value}>{q.value}</SelectItem>))
                      ) : (
                        <>
                          <SelectItem value="high_school">High School</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="diploma">Diploma</SelectItem>
                          <SelectItem value="bachelors_3">Bachelor 3 Years</SelectItem>
                          <SelectItem value="bachelors_4">Bachelor 4 Years</SelectItem>
                          <SelectItem value="masters">Masters</SelectItem>
                          <SelectItem value="phd">PhD</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Qualification Status</Label>
                  <Select value={formData.qualification_status} onValueChange={(v) => setFormData({ ...formData, qualification_status: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="incomplete">Incomplete</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Subject</Label>
                  <Input value={formData.last_qualification_subject} onChange={(e) => setFormData({ ...formData, last_qualification_subject: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Faculty</Label>
                  <Input value={formData.last_qualification_faculty} onChange={(e) => setFormData({ ...formData, last_qualification_faculty: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Grades/GPA</Label>
                  <Input value={formData.last_qualification_grades} onChange={(e) => setFormData({ ...formData, last_qualification_grades: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Qualification Year</Label>
                  <Input value={formData.last_qualification_year} onChange={(e) => setFormData({ ...formData, last_qualification_year: e.target.value })} placeholder="e.g., 2020" />
                </div>
                <div className="space-y-2">
                  <Label>Last Institution Name</Label>
                  <Input value={formData.last_institution} onChange={(e) => setFormData({ ...formData, last_institution: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Institution City</Label>
                  {masterData.cities.length > 0 ? (
                    <Select value={formData.last_institution_city} onValueChange={(v) => setFormData({ ...formData, last_institution_city: v })}>
                      <SelectTrigger><SelectValue placeholder="Select city" /></SelectTrigger>
                      <SelectContent>
                        {masterData.cities.map((c) => (<SelectItem key={c.id} value={c.value}>{c.value}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={formData.last_institution_city} onChange={(e) => setFormData({ ...formData, last_institution_city: e.target.value })} placeholder="Enter city" />
                  )}
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 5: English Test */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <FileCheck className="w-4 h-4" />
                Section 5: English Test
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>English Test Type</Label>
                  <Select value={formData.english_test_type} onValueChange={(v) => setFormData({ ...formData, english_test_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ielts">IELTS</SelectItem>
                      <SelectItem value="toefl">TOEFL</SelectItem>
                      <SelectItem value="pte">PTE</SelectItem>
                      <SelectItem value="duolingo">Duolingo</SelectItem>
                      <SelectItem value="none_required">None Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Test Score</Label>
                  <Input value={formData.english_test_score} onChange={(e) => setFormData({ ...formData, english_test_score: e.target.value })} placeholder="e.g., 7.5" />
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 6: University & Course Selection */}
            <div className="space-y-4">
              <h3 className="font-semibold flex items-center gap-2 text-primary">
                <Building className="w-4 h-4" />
                Section 6: University & Course Selection
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>University</Label>
                  <Select value={formData.university_id} onValueChange={(v) => setFormData({ ...formData, university_id: v, program_id: '', intake_id: '' })}>
                    <SelectTrigger><SelectValue placeholder="Select university" /></SelectTrigger>
                    <SelectContent>
                      {masterData.universities.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Program</Label>
                  <SearchableSelect
                    value={formData.program_id}
                    onChange={(v) => setFormData({ ...formData, program_id: v })}
                    options={filteredPrograms.map((p) => ({ value: p.id, label: p.name }))}
                    placeholder="Select program"
                    searchPlaceholder="Search programs..."
                  />
                </div>
                <div className="space-y-2">
                  <Label>Intake</Label>
                  <Select value={formData.intake_id} onValueChange={(v) => setFormData({ ...formData, intake_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select intake" /></SelectTrigger>
                    <SelectContent>
                      {filteredIntakes.map((i) => (<SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Degree Type</Label>
                  <Select value={formData.degree_type} onValueChange={(v) => setFormData({ ...formData, degree_type: v })}>
                    <SelectTrigger><SelectValue placeholder="Select degree type" /></SelectTrigger>
                    <SelectContent>
                      {masterData.degree_types.map((d) => (<SelectItem key={d.id} value={d.value}>{d.value}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Section 7: Document Upload */}
            {!editingApp && (
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2 text-primary">
                  <Upload className="w-4 h-4" />
                  Section 7: Attach Documents
                </h3>
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="grid grid-cols-3 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={pendingDocType} onValueChange={setPendingDocType}>
                        <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="passport">Passport</SelectItem>
                          <SelectItem value="nic">NIC / ID Card</SelectItem>
                          <SelectItem value="cv">CV / Resume</SelectItem>
                          <SelectItem value="degree">Degree Certificate</SelectItem>
                          <SelectItem value="transcript">Transcript</SelectItem>
                          <SelectItem value="english_test">English Test Score</SelectItem>
                          <SelectItem value="bank_statement">Bank Statement</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Select File</Label>
                      <Input 
                        type="file" 
                        onChange={(e) => setPendingDocFile(e.target.files[0])}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                    </div>
                    <Button type="button" onClick={addPendingDocument} disabled={!pendingDocFile} variant="secondary">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Document
                    </Button>
                  </div>
                  
                  {pendingDocuments.length > 0 && (
                    <div className="mt-4 space-y-2">
                      <Label className="text-sm text-muted-foreground">Documents to upload ({pendingDocuments.length})</Label>
                      <div className="space-y-2">
                        {pendingDocuments.map((doc, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-background rounded border">
                            <div className="flex items-center gap-2">
                              <FileText className="w-4 h-4 text-blue-500" />
                              <span className="text-sm font-medium">{doc.name}</span>
                              {doc.type && <Badge variant="outline" className="text-xs">{doc.type}</Badge>}
                            </div>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removePendingDocument(index)}>
                              <X className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {pendingDocuments.length === 0 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      No documents added yet. You can add documents now or upload them later.
                    </p>
                  )}
                </div>
              </div>
            )}

            <Separator />

            {/* Additional Info */}
            <div className="space-y-4">
              <h3 className="font-semibold">Additional Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Initial Tab</Label>
                  <Select value={formData.agent_tab} onValueChange={(v) => setFormData({ ...formData, agent_tab: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enquiry">Enquiry</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Any additional notes..." rows={3} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); setEditingApp(null); resetFormData(); }}>
                Cancel
              </Button>
              <Button type="submit">
                {editingApp ? 'Update Application' : 'Create Application'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transfer Confirmation Dialog */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{transferringApp?.transferred_to_org ? 'Re-Forward Application' : 'Transfer Application'}</DialogTitle>
            <DialogDescription>
              {transferringApp?.transferred_to_org 
                ? 'Are you sure you want to re-forward this application with the updated information?'
                : 'Are you sure you want to transfer this application to your parent organization?'
              }
            </DialogDescription>
          </DialogHeader>
          {transferringApp && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{transferringApp.student_name}</p>
              <p className="text-sm text-muted-foreground">{transferringApp.student_email}</p>
              {transferringApp.transferred_to_org && (
                <Badge variant="outline" className="mt-2 text-orange-600 border-orange-300">
                  Previously forwarded on {new Date(transferringApp.transfer_date).toLocaleDateString()}
                </Badge>
              )}
            </div>
          )}
          <div className={`p-4 rounded-lg border ${transferringApp?.transferred_to_org ? 'bg-orange-500/10 border-orange-500/20' : 'bg-amber-500/10 border-amber-500/20'}`}>
            <p className={`text-sm ${transferringApp?.transferred_to_org ? 'text-orange-600' : 'text-amber-500'}`}>
              {transferringApp?.transferred_to_org 
                ? 'The organization will be notified about the updated application. Your changes will be synced to their records.'
                : 'Once transferred, this application will appear in your organization\'s application grid. You can still edit and re-forward it later.'
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTransferOpen(false); setTransferringApp(null); }}>
              Cancel
            </Button>
            <Button onClick={handleTransfer} className={transferringApp?.transferred_to_org ? 'bg-orange-600 hover:bg-orange-700' : ''}>
              {transferringApp?.transferred_to_org ? <RotateCcw className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              {transferringApp?.transferred_to_org ? 'Re-Forward' : 'Transfer to Organization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a document for this application</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUploadDocument} className="space-y-4">
            <div className="space-y-2">
              <Label>Document File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={uploadDocType} onValueChange={setUploadDocType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {/* Show required docs first if available */}
                  {requiredDocs.length > 0 && (
                    <>
                      <SelectItem value="_header_required" disabled className="font-semibold text-primary">— Required Documents —</SelectItem>
                      {requiredDocs.map((doc, idx) => (
                        <SelectItem key={`req_${idx}`} value={doc}>{doc}</SelectItem>
                      ))}
                    </>
                  )}
                  {/* Show all document types from master data */}
                  {masterData.document_types.length > 0 && (
                    <>
                      <SelectItem value="_header_all" disabled className="font-semibold text-muted-foreground">— All Document Types —</SelectItem>
                      {masterData.document_types.map((dt) => (
                        <SelectItem key={dt.id} value={dt.value}>{dt.value}</SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setUploadOpen(false); setUploadFile(null); }}>
                Cancel
              </Button>
              <Button type="submit" disabled={uploading || !uploadFile}>
                {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Upload
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credits Purchase Dialog */}
      <Dialog open={creditsOpen} onOpenChange={setCreditsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-500" />
              Purchase AI Credits
            </DialogTitle>
            <DialogDescription>
              Buy credits to use AI-powered document scanning and extraction features
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="bg-emerald-500/10 rounded-lg p-4 border border-emerald-500/20">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-muted-foreground">Current Balance</span>
                <span className="text-2xl font-bold text-emerald-600">{credits.balance} credits</span>
              </div>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Total Purchased: {credits.total_purchased}</span>
                <span>Total Used: {credits.total_used}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Label>Select Credit Package</Label>
              <div className="grid grid-cols-3 gap-3">
                {[10, 25, 50].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    variant={purchaseAmount === amount ? 'default' : 'outline'}
                    onClick={() => setPurchaseAmount(amount)}
                    className="flex flex-col h-auto py-3"
                  >
                    <span className="text-lg font-bold">{amount}</span>
                    <span className="text-xs opacity-70">${(amount * 0.5).toFixed(2)}</span>
                  </Button>
                ))}
              </div>
            </div>

            <div className="bg-muted rounded-lg p-3 text-sm">
              <p className="font-medium mb-2">Credit Costs:</p>
              <ul className="space-y-1 text-muted-foreground text-xs">
                <li>• AI Counselor (Full Profile Analysis): 3 credits</li>
                <li>• Price per credit: $0.50</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditsOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePurchaseCredits} disabled={purchasing} className="bg-emerald-600 hover:bg-emerald-700">
              {purchasing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <DollarSign className="w-4 h-4 mr-1" />
              Pay ${(purchaseAmount * 0.5).toFixed(2)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat Dialog with Counselor Selection */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-blue-500" />
              {chatView === 'select' ? 'Select Team Member' : `Chat with ${selectedCounselor?.name || 'Team'}`}
            </DialogTitle>
            <DialogDescription>
              {chatView === 'select' 
                ? 'Choose a counselor or team member to chat with' 
                : 'Send messages regarding this application'}
            </DialogDescription>
          </DialogHeader>

          {chatView === 'select' ? (
            /* Counselor Selection View */
            <div className="space-y-3">
              {orgTeamMembers.length > 0 ? (
                <div className="grid gap-2">
                  {orgTeamMembers.map((member) => (
                    <Button
                      key={member.id}
                      variant="outline"
                      className="justify-start h-auto py-3 px-4"
                      onClick={() => selectCounselor(member)}
                    >
                      <div className="flex items-center gap-3 w-full">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-amber-500 flex items-center justify-center text-white font-bold">
                          {member.name?.charAt(0)?.toUpperCase() || 'T'}
                        </div>
                        <div className="text-left">
                          <p className="font-medium">{member.name}</p>
                          <p className="text-xs text-muted-foreground">{member.email || 'Team Member'}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                      </div>
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No team members available</p>
                </div>
              )}
              
              {/* Show recent messages if any */}
              {chatMessages.length > 0 && (
                <div className="border-t pt-3 mt-3">
                  <p className="text-sm font-medium mb-2">Recent Messages</p>
                  <div className="text-sm text-muted-foreground">
                    {chatMessages.slice(-2).map((msg) => (
                      <p key={msg.id} className="truncate">
                        <span className="font-medium">{msg.sender_name}:</span> {msg.message}
                      </p>
                    ))}
                  </div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 h-auto mt-1"
                    onClick={() => setChatView('chat')}
                  >
                    View all messages →
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Chat Messages View */
            <div className="flex flex-col h-[400px]">
              {/* Back button */}
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-fit mb-2 -mt-2"
                onClick={() => setChatView('select')}
              >
                ← Back to team members
              </Button>
              
              <ScrollArea className="flex-1 p-4 border rounded-lg bg-muted/20">
                {chatMessages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                    <MessageCircle className="w-12 h-12 mb-2 opacity-30" />
                    <p className="text-sm">No messages yet</p>
                    <p className="text-xs">Start the conversation with {selectedCounselor?.name}!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {chatMessages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex flex-col ${msg.sender_type === 'agent' ? 'items-end' : 'items-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 ${
                            msg.sender_type === 'agent'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                        </div>
                        <span className="text-xs text-muted-foreground mt-1">
                          {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString()}
                        </span>
                      </div>
                    ))}
                    <div ref={chatEndRef} />
                  </div>
                )}
              </ScrollArea>
              <div className="flex gap-2 mt-3">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  placeholder={`Message ${selectedCounselor?.name || 'team'}...`}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendChatMessage()}
                />
                <Button onClick={sendChatMessage} disabled={chatSending || !chatInput.trim()}>
                  {chatSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Reminders
            </DialogTitle>
            <DialogDescription>
              Set and view reminders for this application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Create New Reminder */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <Label className="font-medium">Create New Reminder</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={newReminder.date}
                  onChange={(e) => setNewReminder({ ...newReminder, date: e.target.value })}
                  className="flex-1"
                />
                <Input
                  placeholder="Message (optional)"
                  value={newReminder.message}
                  onChange={(e) => setNewReminder({ ...newReminder, message: e.target.value })}
                  className="flex-1"
                />
                <Button onClick={createReminder} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Existing Reminders */}
            <div className="space-y-2">
              <Label className="font-medium">Existing Reminders</Label>
              {loadingReminders ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : reminders.length > 0 ? (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {reminders.map((r) => (
                      <div key={r.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                        <div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{new Date(r.reminder_date).toLocaleDateString()}</span>
                            <Badge variant={r.status === 'sent' ? 'default' : 'secondary'} className="text-xs">
                              {r.status}
                            </Badge>
                          </div>
                          {r.message && <p className="text-sm text-muted-foreground mt-1">{r.message}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-4 text-muted-foreground">
                  <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No reminders set</p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity Log Dialog */}
      <Dialog open={activityOpen} onOpenChange={setActivityOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-slate-500" />
              Activity Log
            </DialogTitle>
            <DialogDescription>
              View all activities for this application
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {loadingActivity ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : activityLogs.length > 0 ? (
              <ScrollArea className="h-[350px]">
                <div className="space-y-3">
                  {activityLogs.map((log) => (
                    <div key={log.id} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{log.action?.replace(/_/g, ' ')}</p>
                        <p className="text-sm text-muted-foreground">{log.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(log.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <History className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p>No activity recorded yet</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Counselor Dialog */}
      <Dialog open={aiCounselorOpen} onOpenChange={setAiCounselorOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-6 h-6 text-amber-500" />
              AI Counselor
              <Badge variant="outline" className="ml-2">3 credits</Badge>
            </DialogTitle>
            <DialogDescription>
              Upload student documents and let AI analyze & suggest the best programs
            </DialogDescription>
          </DialogHeader>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-4 py-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  aiCounselorStep >= step ? 'bg-amber-500 text-white' : 'bg-muted text-muted-foreground'
                }`}>
                  {step}
                </div>
                <span className={`ml-2 text-sm ${aiCounselorStep >= step ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step === 1 ? 'Select University' : step === 2 ? 'Upload Documents' : 'Review & Create'}
                </span>
                {step < 3 && <div className="w-8 h-0.5 bg-muted mx-2" />}
              </div>
            ))}
          </div>

          {/* Step 1: Select University */}
          {aiCounselorStep === 1 && (
            <div className="space-y-4">
              <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20">
                <p className="text-sm flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  Your credit balance: <strong>{credits.balance}</strong> (This feature costs 3 credits)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Select Target University</Label>
                <Select value={aiSelectedUniversity?.id || ''} onValueChange={(v) => {
                  const uni = masterData.universities.find(u => u.id === v);
                  setAiSelectedUniversity(uni);
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a university" />
                  </SelectTrigger>
                  <SelectContent>
                    {masterData.universities.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setAiCounselorOpen(false)}>Cancel</Button>
                <Button onClick={() => setAiCounselorStep(2)} disabled={!aiSelectedUniversity}>
                  Next: Upload Documents
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 2: Upload Documents */}
          {aiCounselorStep === 2 && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="font-medium mb-2">Selected: {aiSelectedUniversity?.name}</p>
                <p className="text-sm text-muted-foreground">
                  Upload the student's documents (transcripts, degrees, CV, test scores). AI will extract their profile and suggest programs.
                </p>
              </div>

              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <Files className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <Label htmlFor="ai-doc-upload" className="cursor-pointer">
                  <span className="text-primary font-medium">Click to upload</span> or drag and drop
                </Label>
                <Input
                  id="ai-doc-upload"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.zip,.rar,.doc,.docx"
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    setAiUploadedFiles(prev => [...prev, ...files]);
                  }}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Supported: Images (JPG, PNG), PDF, ZIP (auto-extracts files), DOC/DOCX
                </p>
              </div>

              {aiUploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label>Uploaded Files ({aiUploadedFiles.length})</Label>
                  <div className="space-y-1">
                    {aiUploadedFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-muted rounded">
                        <div className="flex items-center gap-2">
                          {file.name.toLowerCase().endsWith('.zip') && (
                            <Badge variant="outline" className="text-xs bg-amber-100 text-slate-800">ZIP</Badge>
                          )}
                          <span className="text-sm truncate">{file.name}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => {
                          setAiUploadedFiles(prev => prev.filter((_, i) => i !== idx));
                        }}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {aiProcessing && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Analyzing documents...</span>
                    <span>{aiProgress}%</span>
                  </div>
                  <Progress value={aiProgress} className="h-2" />
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setAiCounselorStep(1)}>Back</Button>
                <Button 
                  onClick={handleAiCounselorAnalyze} 
                  disabled={aiUploadedFiles.length === 0 || aiProcessing || credits.balance < 3}
                  className="bg-amber-500 hover:bg-slate-800"
                >
                  {aiProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
                  Analyze with AI (3 credits)
                </Button>
              </DialogFooter>
            </div>
          )}

          {/* Step 3: Review & Create */}
          {aiCounselorStep === 3 && aiExtractedProfile && (
            <div className="space-y-4">
              {/* Extracted Profile */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Extracted Student Profile
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    {aiExtractedProfile.personal && Object.entries(aiExtractedProfile.personal).map(([k, v]) => v && (
                      <div key={k}>
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}: </span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                    {aiExtractedProfile.education && Object.entries(aiExtractedProfile.education).map(([k, v]) => v && (
                      <div key={k}>
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}: </span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                    {aiExtractedProfile.test_scores && Object.entries(aiExtractedProfile.test_scores).map(([k, v]) => v && (
                      <div key={k}>
                        <span className="text-muted-foreground capitalize">{k.replace(/_/g, ' ')}: </span>
                        <span className="font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Suggested Programs */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <GraduationCap className="w-4 h-4" />
                    AI Suggested Programs
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {aiSuggestedPrograms.length > 0 ? (
                    <div className="space-y-3">
                      {aiSuggestedPrograms.map((prog, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="font-medium">{prog.program_name}</p>
                            <p className="text-sm text-muted-foreground">{prog.match_reason}</p>
                            <Badge variant="outline" className="mt-1">{prog.degree_type}</Badge>
                          </div>
                          <Button 
                            size="sm" 
                            onClick={() => handleAiCounselorCreateApp(null, prog.degree_type)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Create
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No specific programs suggested. You can still create an application.</p>
                  )}
                </CardContent>
              </Card>

              <DialogFooter>
                <Button variant="outline" onClick={() => { resetAiCounselor(); setAiCounselorOpen(false); }}>
                  Cancel
                </Button>
                <Button onClick={() => handleAiCounselorCreateApp(null, aiExtractedProfile?.recommended_degree_type)}>
                  Create Application
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Floating Action Bubbles */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
        {/* AI Counselor Floating Bubble */}
        <div className="relative">
          {floatingAiOpen && (
            <div className="absolute bottom-16 right-0 w-80 bg-background border rounded-lg shadow-xl p-4 mb-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Brain className="w-4 h-4 text-amber-500" />
                  AI Counselor
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setFloatingAiOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Let AI analyze student documents and suggest the best programs. Costs 3 credits.
              </p>
              <div className="flex items-center justify-between text-sm mb-3">
                <span>Your credits:</span>
                <Badge variant="outline">{credits.balance}</Badge>
              </div>
              <Button 
                className="w-full bg-amber-500 hover:bg-slate-800"
                onClick={() => { setFloatingAiOpen(false); resetAiCounselor(); setAiCounselorOpen(true); }}
                disabled={credits.balance < 3}
              >
                <Brain className="w-4 h-4 mr-2" />
                Start AI Counselor
              </Button>
              {credits.balance < 3 && (
                <Button 
                  variant="link" 
                  className="w-full mt-2 text-xs"
                  onClick={() => { setFloatingAiOpen(false); setCreditsOpen(true); }}
                >
                  Buy more credits
                </Button>
              )}
            </div>
          )}
          <Button
            size="lg"
            className="w-14 h-14 rounded-full bg-amber-500 hover:bg-slate-800 shadow-lg"
            onClick={() => setFloatingAiOpen(!floatingAiOpen)}
          >
            <Brain className="w-6 h-6" />
          </Button>
        </div>

        {/* Chat Floating Bubble */}
        <div className="relative">
          {floatingChatOpen && (
            <div className="absolute bottom-16 right-0 w-80 bg-background border rounded-lg shadow-xl p-4 mb-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-blue-500" />
                  Quick Chat
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setFloatingChatOpen(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Select an application to chat with the organization team.
              </p>
              {(dashboardStats.recent_applications?.length > 0 || applications.length > 0) ? (
                <ScrollArea className="h-[200px]">
                  <div className="space-y-2">
                    {(applications.length > 0 ? applications : dashboardStats.recent_applications || []).slice(0, 5).map((app) => (
                      <Button
                        key={app.id}
                        variant="outline"
                        className="w-full justify-start h-auto py-2"
                        onClick={() => { setFloatingChatOpen(false); openChat(app.id); }}
                      >
                        <div className="text-left">
                          <p className="font-medium text-sm">{app.student_name}</p>
                          <p className="text-xs text-muted-foreground">{app.university_name || 'No university'}</p>
                        </div>
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No applications yet. Create one to start chatting.
                </p>
              )}}
            </div>
          )}
          <Button
            size="lg"
            className="w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg relative"
            onClick={() => setFloatingChatOpen(!floatingChatOpen)}
          >
            <MessageCircle className="w-6 h-6" />
            {unreadChatCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                {unreadChatCount > 9 ? '9+' : unreadChatCount}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AgentPortalPage;
